// e-Signatures — everything out for signature, agency-wide. A request may
// belong to a client (started from their Documents tab) or to no one (a
// staffing agreement, an offer letter, anything sent to someone by email).
// Route: /esign
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchClients } from '../../lib/supabase';
import { listAllRequests, REQUEST_KINDS } from '../../lib/documents/packets';
import { normalizeLang } from '../../lib/documents/i18n';
import OpenLink from '../../components/OpenLink';

const KIND_TITLE = { intake: 'Family forms — initial', renewal: 'Family forms — renewal', transfer: 'Family forms — transfer', lon: 'LON acknowledgment', other: 'Document' };
// Requests from before the kinds existed carry only a packet_type.
const TYPE_TITLE = { gapp_intake: 'Family forms', gapp_transfer: 'Family forms — transfer', private_intake: 'Private-pay intake forms' };
const TABS = [['waiting', 'Waiting on others'], ['mine', 'Waiting on us'], ['completed', 'Completed'], ['draft', 'Drafts']];
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

export default function ESignatures() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState('waiting');
  const [q, setQ] = useState('');
  const [picking, setPicking] = useState(false);

  useEffect(() => { listAllRequests().then(setRows).catch(() => setRows([])); }, []);

  const bucket = (p) => {
    if (p.status === 'completed') return 'completed';
    if (p.status === 'draft' && p._progress.signed === 0) return 'draft';
    if (p._progress.externalOpen > 0) return 'waiting';
    return 'mine'; // staff signatures outstanding, or everything signed and waiting to be filed
  };
  const counts = useMemo(() => (rows || []).reduce((m, p) => { const b = bucket(p); m[b] = (m[b] || 0) + 1; return m; }, {}), [rows]);
  const shown = useMemo(() => (rows || []).filter((p) => bucket(p) === tab).filter((p) => {
    if (!q.trim()) return true;
    const hay = `${p.clients?.last_name || ''} ${p.clients?.first_name || ''} ${p.clients?.client_id || ''} ${p.subject || ''} ${p.recipient_name || ''} ${p.title || ''}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  }), [rows, tab, q]);

  const title = (p) => p.subject || KIND_TITLE[p.request_kind] || REQUEST_KINDS[p.request_kind]?.label || p.title || TYPE_TITLE[p.packet_type] || 'Request';
  const who = (p) => (p.clients ? `${p.clients.last_name}, ${p.clients.first_name} · ${p.clients.client_id}` : p.recipient_name ? `To ${p.recipient_name}${p.recipient_email ? ` · ${p.recipient_email}` : ''}` : 'Not tied to a client');

  return (
    <div style={{ padding: 4, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>e-Signatures</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Everything out for signature, agency-wide — a client's forms, or a document sent to anyone by email. Each comes back as its own PDF.</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setPicking(true)}>+ New request</button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer', fontSize: 13, background: tab === k ? 'var(--accent)' : 'var(--card-bg)', color: tab === k ? '#fff' : 'var(--text)' }}>
            {l}{counts[k] ? <span style={{ marginLeft: 6, opacity: .8 }}>{counts[k]}</span> : null}
          </button>
        ))}
        <input className="form-input" placeholder="Search client, recipient, subject…" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginLeft: 'auto', maxWidth: 280 }} />
      </div>

      <div className="card" style={{ border: '0.5px solid var(--accent-border)' }}>
        {rows === null && <div style={{ padding: 16, fontSize: 13, color: 'var(--text3)' }}>Loading…</div>}
        {rows && shown.length === 0 && <div style={{ padding: 16, fontSize: 13, color: 'var(--text3)' }}>Nothing here.</div>}
        {shown.map((p) => {
          const pr = p._progress; const done = p.status === 'completed';
          return (
            <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 16px', alignItems: 'center', padding: '10px 18px', borderTop: '1px solid var(--border)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {title(p)}
                  {normalizeLang(p.language) === 'es' && <span style={chip}>ES</span>}
                  <span style={{ ...pill, background: done ? 'var(--success-bg)' : p.status === 'draft' ? 'var(--bg2)' : 'var(--warn-bg)', color: done ? 'var(--success)' : p.status === 'draft' ? 'var(--text2)' : 'var(--warn)' }}>
                    {done ? `completed ${fmt(p.completed_at)}` : p.status === 'draft' && pr.signed === 0 ? 'draft' : `${pr.signed} / ${pr.total} signed${pr.externalOpen ? ` · ${pr.externalOpen} with ${p.clients ? 'parent' : 'recipient'}` : ''}${pr.staffOpen ? ` · ${pr.staffOpen} with us` : ''}${pr.total && pr.signed === pr.total ? ' · ready to file' : ''}`}
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>{who(p)} · created {fmt(p.created_at)}{p.pa_cycles?.label ? ` · ${p.pa_cycles.label}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {done && p.pa_cycles?.drive_folder_id && <OpenLink href={`https://drive.google.com/drive/folders/${p.pa_cycles.drive_folder_id}`} className="btn-ghost">Files</OpenLink>}
                {done && !p.pa_cycles?.drive_folder_id && p.final_pdf_url && <OpenLink href={p.final_pdf_url} className="btn-ghost">PDF</OpenLink>}
                <button className={`btn btn-sm ${done ? 'btn-ghost' : 'btn-outline'}`} onClick={() => navigate(`/packets/${p.id}`)}>{done ? 'Open' : 'Continue'}</button>
              </div>
            </div>
          );
        })}
      </div>

      {picking && <NewRequestPicker onClose={() => setPicking(false)} onPick={(client) => navigate(client ? `/packets/new?client=${client.id}&scope=doc` : '/packets/new')} />}
    </div>
  );
}

// "+ New request": about a client, or not.
function NewRequestPicker({ onClose, onPick }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const h = setTimeout(async () => { const { data } = await searchClients(q.trim()); setHits(data || []); }, 250);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header"><div className="modal-title">New e-signature request</div><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <button className="btn btn-outline" onClick={() => onPick(null)} style={{ justifyContent: 'flex-start', textAlign: 'left', padding: 12, height: 'auto', whiteSpace: 'normal' }}>
            <div><div style={{ fontWeight: 600 }}>Not about a client</div><div style={{ fontSize: 12, color: 'var(--text3)' }}>An agreement, an offer letter, anything — upload the PDF, place the fields, send to anyone by email. Files under _General.</div></div>
          </button>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Or about a client</label>
            <input className="form-input" placeholder="Search by name, G-number or Medicaid ID…" value={q} onChange={(e) => setQ(e.target.value)} />
            {hits.length > 0 && <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, maxHeight: 200, overflow: 'auto' }}>
              {hits.map((c) => <button key={c.id} type="button" onClick={() => onPick(c)} style={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit', fontSize: 13, padding: '7px 10px', border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--card-bg)', cursor: 'pointer', color: 'var(--text)' }}>{c.last_name}, {c.first_name} <span style={{ color: 'var(--text3)' }}>{c.client_id}</span></button>)}
            </div>}
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>A client's family forms are sent from their GAPP Submissions tab; this is for a single document — an LON, a consent.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const pill = { fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10 };
const chip = { fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-border)' };
